import type { AxiosResponse } from "axios";
import * as Device from "expo-device";
import { useEffect, useState } from "react";
import { View } from "react-native";
import { Button } from "~/components/Button";
import { type AppleCredentialResponse, AppleLoginButton } from "~/components/form/auth/AppleLoginButton";
import { PasswordInput } from "~/components/form/PasswordInput";
import { TextInput } from "~/components/form/TextInput";
import { GuestFieldGroup } from "~/components/layout/guest/GuestFieldGroup";
import { Page } from "~/components/layout/Page";
import { Spacer } from "~/components/layout/Spacer";
import { BaseText, Title } from "~/components/layout/text";
import { ErrorMessage } from "~/components/layout/text/ErrorMessage";
import { Only } from "~/components/permission/Only";
import { Gap, Padding } from "~/constants/layout";
import { type ApiError, useApi } from "~/providers/ApiProvider";
import { useErrorBag } from "~/providers/ErrorBagProvider";
import { useOnboarding } from "~/providers/OnboardingProvider";
import { type TokenResponse, useSession } from "~/providers/SessionProvider";
import { empty, filled, isEmail } from "~/util";

type loginMethod = "email" | "facebook" | "apple";

export default function LogIn() {
    const { client } = useApi();
    const { dismiss } = useOnboarding();
    const { authorize, setToken } = useSession();
    const errorBag = useErrorBag<"email" | "password" | "auth" | "social">();
    const [loading, setLoading] = useState<loginMethod | null>(null);
    const [email, setEmail] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const device_name = Device.deviceName ?? Device.modelName ?? `${Device.osName} ${Device.osVersion}`;

    // biome-ignore lint/correctness/useExhaustiveDependencies( errorBag.empty): error bag
    useEffect(() => errorBag.empty(), []);

    const canSubmit =
        isEmail(email) && filled(password) && empty(loading) && !errorBag.hasAny(["email", "password", "auth"]);

    const onFocus = () => {
        errorBag.forget(["social", "auth"]);
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies( errorBag.forget): error bag
    useEffect(() => {
        (email || password) && errorBag.forget(["auth", "social"]);
    }, [email, password]);

    const onLogin = (response: AxiosResponse<TokenResponse>) => {
        setToken(response.data.token);
        authorize();
        dismiss("cycle");
    };

    const onError = (error: ApiError) => {
        if (!error.response?.data?.errors) {
            errorBag.add("email", error.response?.data?.message ?? "Could log into your account.");
        }
    };

    const attemptLogin = async () => {
        if (!isEmail(email)) {
            errorBag.add("email", "Email address is not valid");
        }
        if (empty(password)) {
            errorBag.add("password", "Password must not be blank");
        }

        if (!canSubmit) {
            return;
        }
        setLoading("email");

        await client()
            .post("/login", {
                email,
                password,
                device_name,
            })
            .then(onLogin)
            .catch(onError)
            .finally(() => {
                setLoading(null);
            });
    };

    const _onFacebookLogin = async (code: string) => {
        await client()
            .post("/authenticated/facebook", {
                code,
                device_name,
            })
            .then(onLogin)
            .catch(onError)
            .finally(() => {
                setLoading(null);
            });
    };

    const onAppleLogin = async (response: AppleCredentialResponse) => {
        await client()
            .post("/authenticated/apple", {
                ...response,
                device_name,
            })
            .then(onLogin)
            .catch(onError)
            .finally(() => {
                setLoading(null);
            });
    };

    return (
        <Page gradient spacer>
            <GuestFieldGroup>
                <View style={Gap.xs}>
                    <Title size="xl">Welcome back</Title>
                    <BaseText>Sign in to your QENDO account</BaseText>
                </View>
                <Only when={errorBag.has("social")}>
                    <ErrorMessage>{errorBag.message("social")}</ErrorMessage>
                </Only>
                <TextInput
                    name="email"
                    label="Email Address"
                    autoComplete="username"
                    autoCapitalize={"none"}
                    textContentType="emailAddress"
                    error={errorBag.first("auth")}
                    value={email}
                    onChangeText={setEmail}
                    disabled={filled(loading)}
                    onFocus={onFocus}
                />
                <PasswordInput
                    name="password"
                    autoComplete="password"
                    autoCapitalize={"none"}
                    value={password}
                    onChangeText={setPassword}
                    disabled={filled(loading)}
                    onFocus={onFocus}
                />
                <Button
                    title="Sign in"
                    variant="primary"
                    size="lg"
                    onPress={attemptLogin}
                    loading={loading === "email"}
                    disabled={!canSubmit}
                />
                <Button
                    href="/forgot-password"
                    title="I forgot my password"
                    variant="quiet"
                    size="lg"
                    disabled={filled(loading)}
                />
            </GuestFieldGroup>
            <Spacer />
            <View style={[Padding.horizontal_md, Gap.md, Padding.bottom_xxl]}>
                <AppleLoginButton
                    loading={loading === "apple"}
                    onAuthenticationSuccess={onAppleLogin}
                    onAuthenticationStart={() => setLoading("apple")}
                    onAuthenticationResolved={() => setLoading(null)}
                />
            </View>
        </Page>
    );
}
